import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '.';
import { makeReducer } from './utils';

// Whether the dashboard agent's drawer is open. This lives in redux rather than inside `Drawer`
// because the drawer is mounted ONCE for the whole app (App.tsx, outside <Outlet />) while the
// controls that want to open it — currently the dashboards list's "Build with agent" card — are
// rendered deep inside a route. There is no common ancestor to lift state into short of App
// itself, and the two are far enough apart that prop-threading would touch every layer between.
const ROOT = 'dashboardAgent';

const SET_DRAWER_OPEN = `${ROOT}/SET_DRAWER_OPEN`;
const SET_TEMPLATE_TIP = `${ROOT}/SET_TEMPLATE_TIP`;
const SET_PENDING_PROMPT = `${ROOT}/SET_PENDING_PROMPT`;

// A dashboard just instantiated from a template that still owes the "customize with the agent"
// tip, plus the template's starter prompt to offer if the tip is clicked. Held here, not in router
// state, because the dashboard header that draws the tip isn't mounted until the dashboard loads.
export interface TemplateTip {
  dashboardId: string;
  starterPrompt: string | null;
}

interface DashboardAgentReduxState {
  drawerOpen: boolean;
  templateTip: TemplateTip | null;
  // Text a control outside the drawer wants placed in the agent's prompt box; the drawer consumes
  // and clears it. Empty string: nothing to place, but the handoff itself still happened.
  pendingPrompt: string | null;
}

export const useDashboardAgentDrawer = (): {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const drawerOpen = useSelector((state: RootState) => state.dashboardAgent.drawerOpen);

  const setDrawerOpen = useCallback(
    (open: boolean) => dispatch({ type: SET_DRAWER_OPEN, open }),
    [dispatch]
  );

  return { drawerOpen, setDrawerOpen };
};

export const useTemplateTip = (): {
  templateTip: TemplateTip | null;
  setTemplateTip: (templateTip: TemplateTip | null) => void;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const templateTip = useSelector((state: RootState) => state.dashboardAgent.templateTip);

  const setTemplateTip = useCallback(
    (next: TemplateTip | null) => dispatch({ type: SET_TEMPLATE_TIP, templateTip: next }),
    [dispatch]
  );

  return { templateTip, setTemplateTip };
};

export const usePendingAgentPrompt = (): {
  pendingPrompt: string | null;
  setPendingPrompt: (prompt: string | null) => void;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const pendingPrompt = useSelector((state: RootState) => state.dashboardAgent.pendingPrompt);

  const setPendingPrompt = useCallback(
    (prompt: string | null) => dispatch({ type: SET_PENDING_PROMPT, prompt }),
    [dispatch]
  );

  return { pendingPrompt, setPendingPrompt };
};

// Opening the drawer answers the tip. A drawer that was already open when the tip was set doesn't,
// so the tip still shows there as a reminder.
const setDrawerOpenReducer = (
  state: DashboardAgentReduxState,
  { open }: { open: boolean }
): DashboardAgentReduxState => ({
  ...state,
  drawerOpen: open,
  templateTip: open && !state.drawerOpen ? null : state.templateTip
});

const setTemplateTipReducer = (
  state: DashboardAgentReduxState,
  { templateTip }: { templateTip: TemplateTip | null }
): DashboardAgentReduxState => ({ ...state, templateTip });

const setPendingPromptReducer = (
  state: DashboardAgentReduxState,
  { prompt }: { prompt: string | null }
): DashboardAgentReduxState => ({ ...state, pendingPrompt: prompt });

export default makeReducer<DashboardAgentReduxState>(
  { drawerOpen: false, templateTip: null, pendingPrompt: null },
  {
    [SET_DRAWER_OPEN]: setDrawerOpenReducer,
    [SET_TEMPLATE_TIP]: setTemplateTipReducer,
    [SET_PENDING_PROMPT]: setPendingPromptReducer
  }
);
